import Table, { TableProps } from '@cimpress/react-components/lib/Table';
import momentTimezone from 'moment-timezone';
import React, { Fragment, MouseEvent, useMemo } from 'react';
import { LocaleSettings } from '../../models/location';
import { Shipment, SortableShipmentKey } from '../../ports/ShipmentsService';
import { TablePagination } from './TablePagination';

// tslint:disable-next-line:import-name
import style from './index.module.css';

export enum Columns {
  REFERENCE = 'Reference',
  CARRIER_ACCOUNT_KEY = 'Carrier Account Key',
  STATUS = 'Status',
  TAGS = 'Tags',
  CREATED_AT = 'Created at',
  SORTED_AT = 'Sorted at',
  MANIFESTED_AT = 'Manifested at',
}

export interface InternalShipmentsTableProps {
  localeSettings: LocaleSettings;
  onPageSizeChange: (size: number, page: number) => void;
  onPageChange: (page: number) => void;
  onSortChange: (fields: [SortableShipmentKey, 'ASC' | 'DESC'][]) => void;
  loading?: boolean;
  pages: number;
  data: Shipment[];
  hideColumns?: Columns[];
  className?: string;
  toggleSelection?: (shipment: Shipment) => void;
  toggleAll?: () => void;
  isSelected?: (shipmentId: string) => boolean;
}

// tslint:disable-next-line:function-name
export default function InternalShipmentsTable(
  {
    className,
    localeSettings,
    onPageSizeChange,
    onPageChange,
    onSortChange,
    loading,
    pages,
    data,
    hideColumns,
    toggleAll = () => {},
    toggleSelection = () => {},
    isSelected,
  }: InternalShipmentsTableProps,
) {
  const columns = useMemo(
    () => [
      {
        Header: Columns.REFERENCE,
        accessor: 'references',
        filterable: false,
        sortable: true,
        Cell: info => info.value.join(', '),
      },
      {
        Header: Columns.CARRIER_ACCOUNT_KEY,
        accessor: 'carrierAccountKey',
        filterable: false,
      },
      {
        Header: Columns.STATUS,
        accessor: 'status',
        filterable: false,
      },
      {
        Header: Columns.TAGS,
        accessor: 'tags',
        filterable: false,
        sortable: false,
        Cell: info => (info.value as string[]).map((value, i) => <Fragment key={i}>{value}</Fragment>),
      },
      {
        Header: Columns.CREATED_AT,
        accessor: 'createdAt',
        filterable: false,
        Cell: info => info.value ?
          <>
            {momentTimezone(info.value).fromNow()}
            <br />
            <i>{
              momentTimezone(info.value)
                .tz(localeSettings.timezone)
                .format(`${localeSettings.dateFormat} [at] HH:mm`)
            }</i>
          </> :
          '',
      },
      {
        Header: Columns.SORTED_AT,
        accessor: 'sortedAt',
        filterable: false,
        Cell: info => info.value ?
          <>
            {momentTimezone(info.value).fromNow()}
            <br />
            <i>{
              momentTimezone(info.value)
                .tz(localeSettings.timezone)
                .format(`${localeSettings.dateFormat} [at] HH:mm`)
            }</i>
          </> :
          '',
      },
      {
        Header: Columns.MANIFESTED_AT,
        accessor: 'manifestedAt',
        filterable: false,
        Cell: info => info.value ?
          <>
            {momentTimezone(info.value).fromNow()}
            <br />
            <i>{
              momentTimezone(info.value)
                .tz(localeSettings.timezone)
                .format(`${localeSettings.dateFormat} [at] HH:mm`)
            }</i>
          </> :
          '',
      },
    ] as TableProps['columns'],
    [localeSettings],
  );

  return <Table
    className={`${className ?? ''} ${isSelected ? style.Selectable : ''}`}
    columns={columns?.filter(({ Header }) => !hideColumns?.includes(Header as Columns))}
    onPageSizeChange={onPageSizeChange}
    onPageChange={onPageChange}
    onSortedChange={
      fields => onSortChange(fields.map(rule => [rule.id as SortableShipmentKey, rule.desc ? 'DESC' : 'ASC']))
    }
    keyField="id"
    isSelected={isSelected}
    selectable={!!isSelected}
    toggleSelection={(_key, _shiftPressed, shipment) => toggleSelection(shipment)}
    toggleAll={toggleAll}
    getTrProps={(_: any, rowInfo: any) => {
      return {
        onClick: (_e: MouseEvent, handleOriginal: () => void) => {
          toggleSelection(rowInfo.original);
          if (handleOriginal) {
            handleOriginal();
          }
        },
      };
    }}
    loading={loading}
    pages={pages}
    data={data}
    PaginationComponent={TablePagination}
  />;
}
